import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { multiplaiAPI } from "../lib/api";
import { LogIn, ArrowRight, Link2, Loader2 } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [multiplaiLoading, setMultiplaiLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">U</span>
            </div>
            <span className="text-white font-heading font-bold text-xl tracking-tight">USG</span>
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-4 tracking-tight leading-tight">
            Map Your Business.<br/>Master Your AI Strategy.
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            The Unstoppable Company Game helps leadership teams make strategic AI decisions across every department.
          </p>
          <div className="mt-10 flex flex-col gap-3">
            {["10 Departments, 60 Workflows", "Automate, Augment, or Protect", "AI-Powered Suggestions"].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-slate-300">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">U</span>
            </div>
            <span className="font-heading font-bold text-xl tracking-tight text-slate-900">USG</span>
          </div>

          <h2 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Sign in</h2>
          <p className="text-slate-500 mt-2 mb-8">Welcome back. Continue where you left off.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-white border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 bg-white border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-colors active:scale-[0.98]"
            >
              {loading ? "Signing in..." : (
                <span className="flex items-center justify-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#F8FAFC] px-3 text-slate-400">or</span></div>
          </div>

          <Button
            type="button"
            data-testid="multiplai-sso-btn"
            disabled={multiplaiLoading}
            onClick={async () => {
              setMultiplaiLoading(true);
              try {
                const res = await multiplaiAPI.authorize();
                window.location.href = res.data.authorize_url;
              } catch (err) {
                toast.error(err.response?.data?.detail || "Multiplai SSO not available");
                setMultiplaiLoading(false);
              }
            }}
            variant="outline"
            className="w-full h-11 border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg"
          >
            {multiplaiLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4 mr-2 text-indigo-500" />
            )}
            Sign in with Multiplai
          </Button>

          <p className="mt-6 text-center text-sm text-slate-500">
            Don't have an account?{" "}
            <Link to="/register" className="text-indigo-600 font-medium hover:text-indigo-500" data-testid="register-link">
              Create one <ArrowRight className="inline w-3 h-3" />
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
